import type { ColumnDefinitionEnumType } from '../../../../../../api-ts-packages/cad-api';
import { WorkspaceQueue } from '../../panels/workspace-queue';
import type { WithColumnDefinition } from '../../event-queues/get-event-filter-options';
import { Option } from '../../forms/option';
import React, { PropsWithChildren, useMemo, useState } from 'react';
import { noop } from 'lodash';

export type SetBoloQueueFiltersArguments = {
    dispatchAreaOptions?: WithColumnDefinition<Option>[];
    subdivisionOptions?: WithColumnDefinition<Option>[];
};

type FrSharedQueueFiltersContextState = {
    sortingDirective?: {
        columnDefinitionName?: ColumnDefinitionEnumType;
        sortDirection?: WorkspaceQueue['sortDirection'];
    };
    filterDirective?: {
        dispatchAreaOptions: WithColumnDefinition<Option>[];
        subdivisionOptions: WithColumnDefinition<Option>[];
        filterQuery?: string;
    };
    numberOfItems: number;
};

type FrSharedQueueFiltersContextOperations = {
    setQueueListFilters: (selectedOptions: SetBoloQueueFiltersArguments) => void;
    setQueueListSortingDirective: (
        columnDefinitionName?: ColumnDefinitionEnumType,
        sortDirection?: WorkspaceQueue['sortDirection']
    ) => void;
    setQueueFilterQuery: (query?: string) => void;
    setNumberOfItems: (amount: number) => void;
};

const defaultState: FrSharedQueueFiltersContextOperations & FrSharedQueueFiltersContextState = {
    setQueueListFilters: noop,
    setQueueListSortingDirective: noop,
    setNumberOfItems: noop,
    setQueueFilterQuery: noop,
    numberOfItems: 0,
};

const FrBoloQueueFilterStateWriteContext = React.createContext<FrSharedQueueFiltersContextOperations>(
    defaultState
);

const FrBoloQueueFilterStateReadContext = React.createContext<FrSharedQueueFiltersContextState>(
    defaultState
);

/**
 * This context is a mechanism to sync the filters in the Event Queue Store with those in the
 * Bolo Queue Store. In the components where we set Event Queue Filters, we also call these methods.
 *
 * In the fr-bolo-queue.tsx, we consume the changes and propagate them to the Bolo Queue Store.
 *
 * We store numberOfItems in here, because we cannot create a QueueStoreProvider structure that allows the
 * fr-bolo-list-header-button to stay in sync purely through mobx state.
 */
export const FrSharedQueueFiltersContextProvider = (props: PropsWithChildren<unknown>) => {
    const [filters, setFilters] = useState<FrSharedQueueFiltersContextState>({ numberOfItems: 0 });

    const setQueueListFilters = ({
        dispatchAreaOptions,
        subdivisionOptions,
    }: SetBoloQueueFiltersArguments) => {
        if (!dispatchAreaOptions && !subdivisionOptions) {
            setFilters((last) => ({
                ...last,
                filterDirective: undefined,
            }));

            return;
        }

        setFilters((last) => ({
            ...last,
            filterDirective: {
                subdivisionOptions:
                    subdivisionOptions ?? last?.filterDirective?.subdivisionOptions ?? [],
                dispatchAreaOptions:
                    dispatchAreaOptions ?? last?.filterDirective?.dispatchAreaOptions ?? [],
            },
        }));
    };

    const setQueueListSortingDirective = (
        columnDefinitionName?: ColumnDefinitionEnumType,
        sortDirection?: WorkspaceQueue['sortDirection']
    ) => {
        if (!columnDefinitionName && !sortDirection) {
            setFilters((last) => ({
                ...last,
                sortingDirective: undefined,
            }));
        }

        setFilters((last) => ({
            ...last,
            sortingDirective: {
                columnDefinitionName,
                sortDirection,
            },
        }));
    };

    const setQueueFilterQuery = (query?: string) => {
        setFilters((last) => ({
            ...last,
            filterDirective: {
                ...(last?.filterDirective ?? { dispatchAreaOptions: [], subdivisionOptions: [] }),
                filterQuery: query,
            },
        }));
    };

    const setNumberOfItems = (amount: number) => {
        setFilters((last) => ({ ...last, numberOfItems: amount }));
    };

    const operations = useMemo(
        () => ({
            setQueueListFilters,
            setQueueListSortingDirective,
            setQueueFilterQuery,
            setNumberOfItems,
        }),
        []
    );

    return (
        <FrBoloQueueFilterStateWriteContext.Provider value={operations}>
            <FrBoloQueueFilterStateReadContext.Provider value={filters}>
                {props.children}
            </FrBoloQueueFilterStateReadContext.Provider>
        </FrBoloQueueFilterStateWriteContext.Provider>
    );
};

export const useFrBoloQueueFilterState = () => {
    return React.useContext(FrBoloQueueFilterStateReadContext);
};

export const useSetFrBoloQueueFilterState = () => {
    return React.useContext(FrBoloQueueFilterStateWriteContext);
};
